/**
 * QuickActions — QuickCommandBox, NewActionPanel
 */
import React, { useState } from 'react';
import { api } from '../../services/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import {
  Send, Check, Loader2, X, Plus, Mic, Command, Edit3, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

export function QuickCommandBox({ onComplete }) {
  const [command, setCommand] = useState('');
  const [parsing, setParsing] = useState(false);
  const [result, setResult] = useState(null);

  const handleParse = async () => {
    if (!command.trim()) return;
    setParsing(true);
    try {
      const res = await api.post('/clinical-actions/parse-command', { command, patient_id: null });
      setResult(res.data);
    } catch (err) {
      setResult({ error: err.response?.data?.detail || 'Parse failed', actions: [] });
    } finally {
      setParsing(false);
    }
  };

  const handleCreateAction = async (action) => {
    // This would need patient selection — for now, show what was parsed
    setResult(null);
    setCommand('');
    onComplete();
  };

  return (
    <div className="px-6 pb-3" data-testid="quick-command-box">
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <Command className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={command}
            onChange={e => setCommand(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleParse(); }}
            placeholder="Quick command: 'Refill lisinopril 10mg for HTN' or 'Order CBC and CMP'..."
            className="w-full pl-10 pr-3 py-2 text-sm border rounded-lg bg-background placeholder:text-muted-foreground/50"
            data-testid="quick-command-input"
          />
        </div>
        <Button size="sm" onClick={handleParse} disabled={!command.trim() || parsing} className="gap-1"
          data-testid="quick-command-submit">
          {parsing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          Parse
        </Button>
      </div>
      {result && result.actions?.length > 0 && (
        <div className="mt-2 rounded-lg border bg-blue-50/30 p-3 animate-in fade-in-0 slide-in-from-top-1 duration-100"
          data-testid="command-parse-result">
          <p className="text-[10px] font-semibold text-blue-600 mb-2">Parsed {result.actions.length} action(s) — select patient to create drafts</p>
          {result.actions.map((a, i) => (
            <div key={i} className="flex items-center gap-2 py-1 text-xs">
              <Badge variant="outline" className="text-[9px]">{a.action_type}</Badge>
              <span className="font-medium">{a.details?.medication_name || a.details?.lab_name || a.details?.study_name || a.details?.description || 'Action'}</span>
              {a.diagnosis_suggestion && <Badge variant="outline" className="text-[8px] font-mono">{a.diagnosis_suggestion}</Badge>}
            </div>
          ))}
          {result.note_text && <p className="text-[10px] text-muted-foreground mt-1 italic">{result.note_text}</p>}
          <Button variant="ghost" size="sm" className="text-[10px] mt-1" onClick={() => setResult(null)}>Dismiss</Button>
        </div>
      )}
    </div>
  );
}


/* ═══════════════════════════════════════
   NEW ACTION PANEL (7G)
   ═══════════════════════════════════════ */

export function NewActionPanel({ onClose, onComplete }) {
  const [step, setStep] = useState('type'); // type → details → confirm
  const [actionType, setActionType] = useState('');
  const [patientSearch, setPatientSearch] = useState('');
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [reason, setReason] = useState('refill_request');
  const [reasonText, setReasonText] = useState('');
  const [dxCode, setDxCode] = useState('');
  const [details, setDetails] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Search patients
  React.useEffect(() => {
    if (patientSearch.length < 2) return;
    api.get(`/patients?search=${patientSearch}`).then(r => setPatients(r.data.patients || [])).catch(() => {});
  }, [patientSearch]);

  const handleSubmit = async () => {
    if (!selectedPatient || !actionType || !dxCode) return;
    setSubmitting(true);
    try {
      await api.post('/clinical-actions', {
        patient_id: selectedPatient.id,
        action_type: actionType,
        reason,
        reason_text: reasonText,
        diagnosis_code: dxCode,
        details,
      });
      onComplete();
    } catch (err) {
      console.error('Create action failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const ACTION_OPTIONS = [
    { type: 'refill', label: 'Medication / Refill', icon: Pill },
    { type: 'lab', label: 'Lab Order', icon: FlaskConical },
    { type: 'imaging', label: 'Imaging / X-Ray', icon: ScanLine },
    { type: 'task', label: 'Task / Other', icon: ListChecks },
  ];

  return (
    <div className="px-6 pb-4" data-testid="new-action-panel">
      <div className="rounded-xl border bg-card p-4 shadow-sm animate-in fade-in-0 slide-in-from-top-2 duration-150">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>New Clinical Action</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-accent"><ArrowRight className="w-4 h-4 text-muted-foreground rotate-45" /></button>
        </div>

        {/* Patient Search */}
        <div className="mb-3">
          <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Patient</label>
          {selectedPatient ? (
            <div className="flex items-center gap-2 mt-1">
              <span className="text-sm font-medium">{selectedPatient.name?.first} {selectedPatient.name?.last}</span>
              <Badge variant="outline" className="text-[9px] font-mono">{selectedPatient.mrn}</Badge>
              <button onClick={() => setSelectedPatient(null)} className="text-[9px] text-muted-foreground">change</button>
            </div>
          ) : (
            <div className="mt-1">
              <input type="text" placeholder="Search patient..." value={patientSearch} onChange={e => setPatientSearch(e.target.value)}
                className="w-full text-sm border rounded px-2.5 py-1.5 bg-background" data-testid="new-action-patient-search" />
              {patients.length > 0 && (
                <div className="border rounded mt-1 max-h-32 overflow-y-auto">
                  {patients.slice(0, 5).map(p => (
                    <button key={p.id} onClick={() => { setSelectedPatient(p); setPatientSearch(''); setPatients([]); }}
                      className="w-full text-left px-2 py-1.5 text-xs hover:bg-accent">
                      {p.name?.first} {p.name?.last} — {p.mrn}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Action Type */}
        <div className="mb-3">
          <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Action Type</label>
          <div className="flex gap-1.5 mt-1">
            {ACTION_OPTIONS.map(opt => (
              <button key={opt.type} onClick={() => setActionType(opt.type)}
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium ${actionType === opt.type ? 'bg-foreground text-background' : 'bg-muted text-muted-foreground hover:text-foreground'}`}
                data-testid={`action-type-${opt.type}`}>
                <opt.icon className="w-3 h-3" /> {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Reason */}
        <div className="mb-3">
          <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Reason</label>
          <select value={reason} onChange={e => setReason(e.target.value)}
            className="w-full mt-1 text-xs border rounded px-2 py-1.5 bg-background">
            <option value="refill_request">Refill Request</option>
            <option value="patient_called">Patient Called</option>
            <option value="verbal_order">Verbal Order</option>
            <option value="nurse_request">Nurse Request</option>
            <option value="follow_up_monitoring">Follow-up Monitoring</option>
            <option value="result_follow_up">Result Follow-up</option>
            <option value="other">Other</option>
          </select>
          <input type="text" placeholder="Additional context..." value={reasonText} onChange={e => setReasonText(e.target.value)}
            className="w-full mt-1 text-xs border rounded px-2 py-1.5 bg-background" />
        </div>

        {/* Diagnosis */}
        <div className="mb-3">
          <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Diagnosis Code</label>
          <input type="text" placeholder="ICD-10 code (e.g., E11.9, I10)..." value={dxCode} onChange={e => setDxCode(e.target.value)}
            className="w-full mt-1 text-xs border rounded px-2 py-1.5 bg-background font-mono" data-testid="new-action-dx" />
        </div>

        {/* Action-specific details */}
        {actionType === 'refill' && (
          <div className="mb-3 space-y-1.5">
            <input type="text" placeholder="Medication name..." value={details.medication_name || ''} onChange={e => setDetails(d => ({ ...d, medication_name: e.target.value }))}
              className="w-full text-xs border rounded px-2 py-1.5 bg-background" data-testid="new-action-med-name" />
            <div className="grid grid-cols-3 gap-1.5">
              <input type="text" placeholder="Dose" value={details.dose || ''} onChange={e => setDetails(d => ({ ...d, dose: e.target.value }))} className="text-xs border rounded px-2 py-1.5 bg-background" />
              <input type="text" placeholder="Qty" value={details.quantity || ''} onChange={e => setDetails(d => ({ ...d, quantity: e.target.value }))} className="text-xs border rounded px-2 py-1.5 bg-background" />
              <input type="text" placeholder="Refills" value={details.refills || ''} onChange={e => setDetails(d => ({ ...d, refills: e.target.value }))} className="text-xs border rounded px-2 py-1.5 bg-background" />
            </div>
          </div>
        )}
        {actionType === 'lab' && (
          <div className="mb-3">
            <input type="text" placeholder="Lab name (e.g., CBC, CMP, HbA1c)..." value={details.lab_name || ''} onChange={e => setDetails(d => ({ ...d, lab_name: e.target.value }))}
              className="w-full text-xs border rounded px-2 py-1.5 bg-background" />
          </div>
        )}
        {actionType === 'imaging' && (
          <div className="mb-3">
            <input type="text" placeholder="Study name (e.g., Chest X-Ray, Knee MRI)..." value={details.study_name || ''} onChange={e => setDetails(d => ({ ...d, study_name: e.target.value }))}
              className="w-full text-xs border rounded px-2 py-1.5 bg-background" />
          </div>
        )}

        {/* Submit */}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={handleSubmit} disabled={!selectedPatient || !actionType || !dxCode || submitting}
            data-testid="new-action-submit">
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
            Create Draft Action
          </Button>
        </div>
      </div>
    </div>
  );
}
